"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import bg from "@/public/text-only/propBg.png";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageTextOnlyLPSchema } from "@/sanity/pages/PageTextOnlyLPTemplate";
import bitcoin from "@/public/text-only/bitcoin.png";

export default function PropTrading({ data }: { data: PageTextOnlyLPSchema }) {
  return (
    <section
      id="propTrading"
      className="w-full h-full px-[4rem] lg:px-[7rem] py-[7.375rem] lg:py-[8.75rem] "
    >
      <div className="relative mx-auto w-full">
        <div
          className="relative bg-[#223D42] rounded-[2.75rem] mx-auto w-[min(97.5rem,100%)] z-[1] flex flex-col items-start
          p-[3rem] lg:p-[4rem] pt-[7.375rem] lg:pt-[8.75rem] "
        >
          <Image
            className="absolute z-0 left-0 bottom-0 !w-full !h-full object-cover object-right-bottom mix-blend-luminosity"
            style={{
              maskImage: "linear-gradient(to top, black 20%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to top, black 20%, transparent 100%)",
            }}
            src={bg}
            quality={100}
            alt="background art"
          />
          <Image
            src={bitcoin}
            alt=""
            className="absolute top-0 -translate-y-1/2 left-[1.62rem] lg:left-[13.62rem] !w-[9.875rem]"
          />
          {data.propTradingTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative z-[1] richtext richtext-start prose-h2:font-medium prose-h2:text-[3rem] lg:prose-h2:text-[4rem] mb-[4rem]
              prose-h2:text-white prose-p:text-white/80 [&_br]:hidden lg:[&_br]:block"
            >
              <PortableText
                value={data.propTradingTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
          {data.propTradingContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative z-[1] richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2rem] prose-p:text-white/80
              prose-strong:text-[2.15rem] prose-strong:font-medium
              [&_.darkGreenBlock]:w-full [&_.darkGreenBlock]:mb-[3rem] prose-p:[&_.darkGreenBlock]:!text-white prose-p:[&_.darkGreenBlock]:!text-[2rem] lg:prose-p:[&_.darkGreenBlock]:!text-[2.25rem]
              [&_.squareTopicListNoDivisor]:w-full
              prose-h3:text-white prose-h3:text-[3rem] prose-h3:font-medium"
            >
              <PortableText
                value={data.propTradingContent}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        <div className="relative mx-auto w-[min(74.25rem,100%)] lg:px-0 z-[1] mt-[4rem] lg:mt-[8rem] flex flex-col items-start gap-[4rem]">
          {data.propTradingChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative z-[1] richtext richtext-start
              prose-p:font-instrument prose-p:text-white/80 lg:max-w-[45.125rem] prose-p:font-normal
              [&_.font-gestura]:font-normal"
            >
              <PortableText
                value={data.propTradingChip}
                components={richTextComponent}
              />
            </motion.div>
          )}
          {data.propTradingTitle2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative z-[1] richtext richtext-start
              prose-h2:font-medium prose-h2:text-[3rem] lg:prose-h2:text-[4rem]
              prose-h2:text-white prose-p:text-white/80"
            >
              <PortableText
                value={data.propTradingTitle2}
                components={richTextComponent}
              />
            </motion.div>
          )}

          <div
            className={`relative w-full overflow-clip flex items-center h-[0.75rem]`}
          >
            <div className="absolute left-0 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] rotate-45 bg-[#28454B]" />
            <div className="w-full bg-[#28454B] h-[0.125rem]" />
            <div className="absolute right-0 translate-x-1/2 top-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] rotate-45 bg-[#28454B]" />
          </div>
          {data.propTradingContent2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative z-[1] richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2rem] prose-p:text-white/80
              prose-strong:text-[2.2  5rem] prose-strong:font-medium prose-strong:text-white
              [&_.greenWithBorderBlock]:w-full [&_.greenWithBorderBlock]:mb-[3rem] prose-p:[&_.greenWithBorderBlock]:!text-white
              [&_.squareTopicListNoDivisor]:w-full
              prose-h3:text-white prose-h3:text-[3rem] prose-h3:font-medium
              prose-h2:text-white prose-h2:font-medium prose-h2:text-[3.5rem] lg:prose-h2:text-[4rem]"
            >
              <PortableText
                value={data.propTradingContent2}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
